/* Fancy Featured Product — interactive behavior
 * Custom element: <fancy-featured-product>
 *  - cursor glow follows mouse
 *  - closest pin to cursor becomes active while hovering
 *  - magnetic offset bends pins toward cursor (if enabled)
 *  - pins auto-cycle when not hovering (if enabled)
 */
(function () {
  if (customElements.get('fancy-featured-product')) return;

  const MAGNETIC_FACTOR = 0.08;

  class FancyFeaturedProduct extends HTMLElement {
    constructor() {
      super();
      this._onMove = this._onMove.bind(this);
      this._onEnter = this._onEnter.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this._onPinTap = this._onPinTap.bind(this);
      this._onPinKey = this._onPinKey.bind(this);
      this._tick = this._tick.bind(this);
      this._frame = 0;
    }

    connectedCallback() {
      this.autoCycle = this.dataset.autoCycle !== 'false';
      this.cycleInterval = Math.max(400, parseInt(this.dataset.cycleInterval, 10) || 2400);
      this.magnetic = this.dataset.magnetic !== 'false';

      this.pins = Array.from(this.querySelectorAll('.fancy-fp__pin'));
      this.pinData = this.pins.map((el) => {
        const titleEl = el.querySelector('.fancy-fp__pin-title');
        const bodyEl = el.querySelector('.fancy-fp__pin-body');
        return {
          el,
          x: parseFloat(el.dataset.x) || 0,
          y: parseFloat(el.dataset.y) || 0,
          title: titleEl ? titleEl.textContent : '',
          body: bodyEl ? bodyEl.textContent : '',
        };
      });

      this.mobileCard = this.querySelector('.fancy-fp__mobile-card');
      this.mobileTitle = this.querySelector('.fancy-fp__mobile-title');
      this.mobileBody = this.querySelector('.fancy-fp__mobile-body');

      this.activeIndex = 0;
      this.hovering = false;
      this.mouse = { x: 50, y: 50 };

      if (this.pins.length > 0) {
        this._setActive(0, { instant: true });
      }

      this.addEventListener('mousemove', this._onMove);
      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);

      // Tap / keyboard activation on pin rail (primarily mobile, harmless on desktop)
      this.pins.forEach((pin) => {
        pin.addEventListener('click', this._onPinTap);
        pin.addEventListener('keydown', this._onPinKey);
      });

      this._startAutoCycle();
    }

    disconnectedCallback() {
      this.removeEventListener('mousemove', this._onMove);
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      if (this.pins) {
        this.pins.forEach((pin) => {
          pin.removeEventListener('click', this._onPinTap);
          pin.removeEventListener('keydown', this._onPinKey);
        });
      }
      this._stopAutoCycle();
      cancelAnimationFrame(this._frame);
      if (this._swapTimer) clearTimeout(this._swapTimer);
    }

    _onPinTap(e) {
      const pin = e.currentTarget;
      const index = parseInt(pin.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this._setActive(index);
      // Restart auto-cycle from this pin so the user has time to read
      if (this.autoCycle) {
        this._stopAutoCycle();
        this._startAutoCycle();
      }
    }

    _onPinKey(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._onPinTap(e);
      }
    }

    _startAutoCycle() {
      if (!this.autoCycle || this.pinData.length <= 1) return;
      this._stopAutoCycle();
      this._cycleId = setInterval(() => {
        if (this.hovering) return;
        const next = (this.activeIndex + 1) % this.pinData.length;
        this._setActive(next);
      }, this.cycleInterval);
    }

    _stopAutoCycle() {
      if (this._cycleId) {
        clearInterval(this._cycleId);
        this._cycleId = null;
      }
    }

    _onEnter() {
      this.hovering = true;
    }

    _onLeave() {
      this.hovering = false;
      // Reset cursor glow to center and clear magnetic offsets
      this.style.setProperty('--fp-mx', '50%');
      this.style.setProperty('--fp-my', '50%');
      this.pinData.forEach(({ el }) => {
        el.style.setProperty('--fp-pin-dx', '0%');
        el.style.setProperty('--fp-pin-dy', '0%');
      });
    }

    _onMove(e) {
      const rect = this.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const mx = ((e.clientX - rect.left) / rect.width) * 100;
      const my = ((e.clientY - rect.top) / rect.height) * 100;
      this.mouse.x = mx;
      this.mouse.y = my;

      // Throttle DOM writes to a single rAF
      if (this._frame) return;
      this._frame = requestAnimationFrame(this._tick);
    }

    _tick() {
      this._frame = 0;
      const { x: mx, y: my } = this.mouse;

      this.style.setProperty('--fp-mx', mx + '%');
      this.style.setProperty('--fp-my', my + '%');

      if (this.pinData.length === 0) return;

      // Magnetic offset
      if (this.magnetic && this.hovering) {
        this.pinData.forEach(({ el, x, y }) => {
          const dx = (mx - x) * MAGNETIC_FACTOR;
          const dy = (my - y) * MAGNETIC_FACTOR;
          el.style.setProperty('--fp-pin-dx', dx + '%');
          el.style.setProperty('--fp-pin-dy', dy + '%');
        });
      }

      // Closest pin to cursor wins
      if (this.hovering) {
        let closest = 0;
        let dmin = Infinity;
        for (let i = 0; i < this.pinData.length; i++) {
          const p = this.pinData[i];
          const d = Math.hypot(p.x - mx, p.y - my);
          if (d < dmin) { dmin = d; closest = i; }
        }
        if (closest !== this.activeIndex) this._setActive(closest);
      }
    }

    _setActive(index, opts) {
      if (index === this.activeIndex && !(opts && opts.instant)) {
        // Still ensure mobile card matches on first paint
      }
      this.activeIndex = index;
      this.pins.forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
      });
      this._updateMobileCard(opts && opts.instant);
    }

    _updateMobileCard(instant) {
      if (!this.mobileCard || !this.mobileTitle || !this.mobileBody) return;
      const data = this.pinData[this.activeIndex];
      if (!data) return;

      const apply = () => {
        this.mobileTitle.textContent = data.title || '';
        this.mobileBody.textContent = data.body || '';
        this.mobileCard.classList.remove('is-changing');
      };

      if (instant) {
        apply();
        return;
      }

      this.mobileCard.classList.add('is-changing');
      if (this._swapTimer) clearTimeout(this._swapTimer);
      this._swapTimer = setTimeout(apply, 130);
    }
  }

  customElements.define('fancy-featured-product', FancyFeaturedProduct);
})();
